// ─────────────────────────────────────────────────────────────────────────────
// Web Search & Parallel Search Tools
// Supports: Brave Search API, Serper.dev (Google Search API)
// ─────────────────────────────────────────────────────────────────────────────

import type {
	SearchResult,
	SearchOutput,
	WebSearchInput,
	ParallelSearchInput,
	SearchProvider,
} from "../types";

// ── Brave Search ──────────────────────────────────────────────────────────────

async function searchViaBrave(
	query: string,
	apiKey: string,
	numResults = 5
): Promise<SearchResult[]> {
	const url = new URL("https://api.search.brave.com/res/v1/web/search");
	url.searchParams.set("q", query);
	url.searchParams.set("count", String(Math.min(numResults, 20)));

	const response = await fetch(url.toString(), {
		headers: {
			Accept: "application/json",
			"Accept-Encoding": "gzip",
			"X-Subscription-Token": apiKey,
		},
	});

	if (!response.ok) {
		throw new Error(`Brave Search API error: ${response.status} ${response.statusText}`);
	}

	const data = await response.json();
	const webResults = data?.web?.results ?? [];

	return webResults.slice(0, numResults).map((item: Record<string, string>) => ({
		title: item.title ?? "",
		url: item.url ?? "",
		snippet: item.description ?? "",
	}));
}

// ── Serper (Google Search) ────────────────────────────────────────────────────

async function searchViaSerper(
	query: string,
	apiKey: string,
	numResults = 5
): Promise<SearchResult[]> {
	const response = await fetch("https://google.serper.dev/search", {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			"X-API-KEY": apiKey,
		},
		body: JSON.stringify({ q: query, num: Math.min(numResults, 10) }),
	});

	if (!response.ok) {
		throw new Error(`Serper API error: ${response.status} ${response.statusText}`);
	}

	const data = await response.json();
	const organic = data?.organic ?? [];

	return organic.slice(0, numResults).map((item: Record<string, string>) => ({
		title: item.title ?? "",
		url: item.link ?? "",
		snippet: item.snippet ?? "",
	}));
}

// ── Fallback: Wikipedia search (CORS-safe, no API key required) ───────────────
// Uses MediaWiki API with origin=* for browser-side CORS.
// Great for accounting/GAAP/IFRS queries; returns encyclopedic article snippets.

async function searchViaWikipedia(query: string, numResults = 5): Promise<SearchResult[]> {
	const params = new URLSearchParams({
		action: "query",
		list: "search",
		srsearch: query,
		srlimit: String(numResults),
		srprop: "snippet|titlesnippet",
		format: "json",
		origin: "*",
	});
	try {
		const response = await fetch(`https://en.wikipedia.org/w/api.php?${params}`);
		if (!response.ok) return [];
		const data = await response.json();
		const hits: Array<{ title: string; snippet: string; pageid: number }> =
			data?.query?.search ?? [];
		return hits.map((h) => ({
			title: h.title,
			url: `https://en.wikipedia.org/wiki/${encodeURIComponent(h.title.replace(/ /g, "_"))}`,
			snippet: h.snippet.replace(/<[^>]+>/g, ""), // strip <span class="searchmatch"> tags
		}));
	} catch {
		return [];
	}
}

// ── Fallback: DuckDuckGo instant answers ─────────────────────────────────────
// Note: Returns limited data for specific well-known topics only.

async function searchViaDuckDuckGo(query: string): Promise<SearchResult[]> {
	const url = `https://api.duckduckgo.com/?q=${encodeURIComponent(
		query
	)}&format=json&no_html=1&skip_disambig=1`;
	try {
		const response = await fetch(url);
		if (!response.ok) return [];
		const data = await response.json();
		const results: SearchResult[] = [];

		if (data.AbstractText) {
			results.push({
				title: data.Heading ?? query,
				url: data.AbstractURL ?? "",
				snippet: data.AbstractText,
			});
		}

		(data.RelatedTopics ?? []).slice(0, 4).forEach((topic: Record<string, string>) => {
			if (topic.Text) {
				results.push({
					title: topic.Text.split(" - ")[0] ?? topic.Text,
					url: topic.FirstURL ?? "",
					snippet: topic.Text,
				});
			}
		});

		// If no results from DDG, try Wikipedia as a fallback
		if (results.length === 0) {
			return searchViaWikipedia(query, 5);
		}

		return results;
	} catch {
		return searchViaWikipedia(query, 5);
	}
}

// ── Proxy search (production — routes through Netlify Function to avoid CORS) ──

async function searchViaProxy(
	query: string,
	apiKey: string | undefined,
	provider: SearchProvider | undefined,
	numResults: number
): Promise<SearchResult[]> {
	try {
		const response = await fetch("/.netlify/functions/search", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ query, apiKey, provider, numResults }),
		});

		if (!response.ok) {
			// Function not deployed / server error — fall back to Wikipedia
			console.warn(
				`[Search] Netlify function returned ${response.status}, falling back to Wikipedia.`
			);
			return searchViaWikipedia(query, numResults);
		}

		const data = (await response.json()) as {
			query: string;
			results: SearchResult[];
			error?: string;
		};
		const results = data.results ?? [];

		// If the proxy reported an API key error, surface it as the first result
		// so Claude can tell the user what's wrong instead of silently using Wikipedia.
		if (data.error) {
			console.warn(`[Search] Proxy error: ${data.error}`);
			const errorResult: SearchResult = {
				title: "⚠ Search API Error",
				url: "",
				snippet: `${data.error} — Please check your search API key in the key settings.`,
			};
			const wikiResults = await searchViaWikipedia(query, numResults);
			return [errorResult, ...wikiResults];
		}

		// Proxy returned real results
		if (results.length > 0) return results;

		// Empty results with no error — fall back to Wikipedia
		return searchViaWikipedia(query, numResults);
	} catch {
		// Network error reaching the function — fall back to Wikipedia
		return searchViaWikipedia(query, numResults);
	}
}

// ── AI proxy search ──────────────────────────────────────────────────────────
// Preferred path in production. Hits the server-side proxy at `${endpoint}/search`
// — no API key in the browser, session cookie + LoopBack auth header reused.
async function searchViaAIProxy(
	query: string,
	apiEndpoint: string,
	numResults: number
): Promise<SearchResult[]> {
	const base = apiEndpoint.replace(/\/+$/, "");
	const url = base.endsWith("/search") ? base : `${base}/search`;

	const headers: Record<string, string> = { "Content-Type": "application/json" };
	try {
		const token = window.localStorage?.getItem("$LoopBack$accessTokenId");
		if (token) headers.Authorization = token;
	} catch {
		/* localStorage unavailable */
	}

	const response = await fetch(url, {
		method: "POST",
		headers,
		credentials: "same-origin",
		body: JSON.stringify({ query, count: numResults }),
	});
	if (!response.ok) {
		const errorBody = await response.text();
		throw new Error(`AI proxy search ${response.status}: ${errorBody}`);
	}
	const data = await response.json();
	const results: unknown[] = Array.isArray(data?.results) ? data.results : [];
	return results
		.map((r) => {
			const row = r as { title?: string; url?: string; snippet?: string };
			return {
				title: String(row.title ?? ""),
				url: String(row.url ?? ""),
				snippet: String(row.snippet ?? ""),
			};
		})
		.filter((r) => r.url);
}

// ── Core search dispatcher ────────────────────────────────────────────────────

export async function performSearch(
	query: string,
	apiKey: string | undefined,
	provider: SearchProvider | undefined,
	numResults = 5,
	apiEndpoint?: string
): Promise<SearchResult[]> {
	if (!query.trim()) return [];

	// Preferred path: route through the AI proxy (server-side Brave/Serper key).
	if (apiEndpoint) {
		return searchViaAIProxy(query, apiEndpoint, numResults);
	}

	// Netlify functions proxy (legacy deployment).
	if (import.meta.env.PROD) {
		return searchViaProxy(query, apiKey, provider, numResults);
	}

	// Development: call APIs directly
	if (apiKey) {
		if (provider === "brave") {
			return searchViaBrave(query, apiKey, numResults);
		}
		if (provider === "serper") {
			return searchViaSerper(query, apiKey, numResults);
		}
		// Default to Brave if provider not specified but key provided
		try {
			return await searchViaBrave(query, apiKey, numResults);
		} catch {
			return searchViaSerper(query, apiKey, numResults);
		}
	}

	// No key: fall back to DuckDuckGo
	console.warn(
		"[ContextualAIAssistant] No search API key or proxy endpoint provided. Using DuckDuckGo fallback (limited results)."
	);
	return searchViaDuckDuckGo(query);
}

// ── Web Search Tool (single query) ────────────────────────────────────────────

export async function webSearchTool(
	input: WebSearchInput,
	apiKey?: string,
	provider?: SearchProvider,
	apiEndpoint?: string
): Promise<SearchOutput> {
	const results = await performSearch(
		input.query,
		apiKey,
		provider,
		input.num_results ?? 5,
		apiEndpoint
	);
	return { query: input.query, results };
}

// ── Parallel Search Tool (multiple queries concurrently) ──────────────────────

export async function parallelSearchTool(
	input: ParallelSearchInput,
	apiKey?: string,
	provider?: SearchProvider,
	apiEndpoint?: string
): Promise<SearchOutput[]> {
	if (!input.queries || input.queries.length === 0) {
		throw new Error("parallel_search requires at least one query");
	}

	const MAX_CONCURRENT = 5; // cap parallel requests
	const queries = input.queries.slice(0, MAX_CONCURRENT);
	const numResults = input.num_results_per_query ?? 3;

	const promises = queries.map((q) =>
		webSearchTool({ query: q, num_results: numResults }, apiKey, provider, apiEndpoint)
	);
	const results = await Promise.allSettled(promises);

	return results.map((result, i) => {
		if (result.status === "fulfilled") return result.value;
		return {
			query: queries[i],
			results: [],
		};
	});
}

// ── Format search results as readable text ────────────────────────────────────

export function formatSearchResults(output: SearchOutput): string {
	if (output.results.length === 0) {
		return `No results found for: "${output.query}"`;
	}

	const lines = [`Search results for: "${output.query}"\n`];
	output.results.forEach((r, i) => {
		lines.push(`${i + 1}. **${r.title}**`);
		lines.push(`   URL: ${r.url}`);
		lines.push(`   ${r.snippet}\n`);
	});
	return lines.join("\n");
}

export function formatParallelSearchResults(outputs: SearchOutput[]): string {
	return outputs.map(formatSearchResults).join("\n---\n");
}
